import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { getCubeAPI } from '../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { Skeleton, Tooltip } from 'antd'
import { ReportFilterStore } from '../ReportFilterStore'
import { parseAndDownloadCSV, GrPrice } from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { EmployeeCategoryReportModal } from './EmployeeCategoryReportModal'
import Helmet from 'react-helmet'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import moment from 'moment'
import { Track } from '../../../../../Track'
import _ from 'lodash'
import { InsightsRoutes, Routes } from '../../../../../constants/Routes'
import { ReportAlert } from '../ReportAlert'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

interface IOrder {
  field: string
  direction: 'asc' | 'desc'
}

interface State {
  order: IOrder
  selectedEmployee?: {
    id: string
    name: string
  }
  fullQuery: any
  showSaleEmployee: boolean
}

@inject('reportFilterStore')
@observer
export class EmployeeReport extends React.Component<Props, State> {
  // Static property that stores loaded report data
  data: any

  constructor(props: Props) {
    super(props)
    this.state = {
      order: {
        field: 'PaymentLines.net',
        direction: 'desc',
      },
      fullQuery: undefined,
      showSaleEmployee: true,
    }
  }
  componentDidMount = () => this.setQuery()

  setQuery = () => {
    const { startDateString, endDateString, selectedLocationIds } = this.props.reportFilterStore!
    const fullQuery = {
      measures: [
        'PaymentLines.gross',
        'PaymentLines.refunds',
        'PaymentLines.discounts',
        'PaymentLines.net',
        'PaymentLines.barcodePercentage',
        'PaymentLines.averageCartQuantity',
        'Payments.saleCount',
        'PaymentLines.averageCart',
      ],
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: null,
          dateRange: [startDateString, endDateString],
        },
      ],
      dimensions: [this.employeeToReport, 'Users.name'],
      filters: [
        {
          dimension: 'Locations.id',
          operator: 'equals',
          values: selectedLocationIds.map((id) => `${id}`),
        },
        {
          dimension: 'Payments.status',
          operator: 'equals',
          values: [`completed`],
        },
        {
          member: 'Users.name',
          operator: 'set',
        },
      ],
      order: {
        [this.state.order.field]: this.state.order.direction,
      },
      limit: 50000,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    this.setState({ fullQuery })
  }

  toggleOrder(fieldName: string) {
    if (this.state.order.field === fieldName) {
      const currentDirection = this.state.order.direction
      this.setState({
        order: {
          field: fieldName,
          direction: currentDirection === 'asc' ? 'desc' : 'asc',
        },
      })
    } else {
      this.setState({
        order: {
          field: fieldName,
          direction: 'desc',
        },
      })
    }

    this.setQuery()
  }

  convertFieldsToDollar = (data: any) => {
    const dollarFields = [
      'PaymentLines.net',
      'PaymentLines.refunds',
      'PaymentLines.discounts',
      'PaymentLines.averageCart',
    ]
    data.forEach((row: any) => {
      dollarFields.forEach((field) => {
        row[field] = GrPrice.convertDollarToDollar(row[field])
      })
    })
  }

  tableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.rawData()
    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>
                <a onClick={() => this.toggleOrder('Users.name')}>Employee</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.net')}>Net sales</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.refunds')}>Refunds</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.discounts')}>Discounts</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Payments.saleCount')}># of sales</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.averageCart')}>Avg cart</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.averageCartQuantity')}>
                  Avg units
                </a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.barcodePercentage')}>
                  Barcode usage
                </a>
              </th>
              <th className='text-right'>Category sales</th>
            </tr>
          </thead>
          <tbody>
            {rawData.map((data, index) => {
              return (
                <tr key={index}>
                  <td>{data['Users.name']}</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['PaymentLines.net'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['PaymentLines.refunds'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['PaymentLines.discounts'])}
                  </td>
                  <td className='text-right'>{data['Payments.saleCount']}</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['PaymentLines.averageCart'])}
                  </td>
                  <td className='text-right'>
                    {parseFloat(data['PaymentLines.averageCartQuantity']).toFixed(2)}
                  </td>
                  <td className='text-right'>
                    {parseFloat(data['PaymentLines.barcodePercentage'] || 0).toFixed(2)}%
                  </td>
                  <td className='text-right'>
                    <a
                      onClick={() => {
                        this.setState({
                          selectedEmployee: {
                            id: data[this.employeeToReport],
                            name: data['Users.name'],
                          },
                        })
                      }}
                    >
                      View
                    </a>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  renderChart(Component: any) {
    const { startDate, endDate } = this.props.reportFilterStore!
    // eslint-disable-next-line react/prop-types
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        Track.track('dashboard:reports_employees', {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })
        // eslint-disable-next-line react/prop-types
        this.data = resultSet.rawData()
      } else {
        Track.startTimer('dashboard:reports_employees')
        this.data = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  get inSightReportPath(): string {
    return Routes.INSIGHTS_REPORTS(this.props.companyId, InsightsRoutes.EMPLOYEE_SALES)
  }

  get employeeToReport() {
    return this.state.showSaleEmployee ? 'Payments.employeeid' : 'PaymentQueues.userid'
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = _.cloneDeep(this.data)
    this.convertFieldsToDollar(data)
    if (data) {
      parseAndDownloadCSV(`employee-sales-${startDate}-${endDate}`, data)
    }
  }

  render() {
    const { companyId } = this.props
    const { fullQuery } = this.state

    return (
      <>
        <Helmet>
          <title>Reports | Employee Sales</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter reportPath={this.inSightReportPath} />
        <ReportAlert reportPath={this.inSightReportPath} reportType={'insights'} />

        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Employee sales</b>
            </div>
            <div className='text-muted'>Does not include taxes</div>

            <div className='mt-2 mb-2'>
              <UncontrolledButtonDropdown className='mr-2'>
                <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                  {this.state.showSaleEmployee ? 'Sale employee' : 'Floor employee'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    onClick={() => this.setState({ showSaleEmployee: true }, this.setQuery)}
                  >
                    <Tooltip title='The employee who processed the sale'>
                      Sale employee
                      <span className='fa fa-info-circle text-muted ml-2' />
                    </Tooltip>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => this.setState({ showSaleEmployee: false }, this.setQuery)}
                  >
                    <Tooltip
                      title='The employee who created the parked sale'
                      overlayStyle={{ minWidth: 'fit-content' }}
                    >
                      Floor employee
                      <span className='fa fa-info-circle text-muted ml-2' />
                    </Tooltip>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='table-responsive'>
              {fullQuery ? (
                <QueryRenderer
                  query={fullQuery}
                  cubejsApi={getCubeAPI(companyId)}
                  render={this.renderChart(this.tableRenderer)}
                />
              ) : (
                <Skeleton active />
              )}
            </div>
          </div>
        </div>
        {!!this.state.selectedEmployee && (
          <EmployeeCategoryReportModal
            companyId={companyId}
            employeeToReport={this.employeeToReport}
            employeeId={this.state.selectedEmployee.id}
            employeeName={this.state.selectedEmployee.name}
            onClose={() => this.setState({ selectedEmployee: undefined })}
          />
        )}
      </>
    )
  }
}
